import React from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Formik,
  Field,
} from 'formik';
import {
  toast,
} from 'react-toastify';
import * as Yup from 'yup';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  Flex,
  Text,
  icons,
} from 'web-styled';

import {
  Button,
  TextField,
  SelectField,
  DatePickerField,
  UploadPhoto,
} from 'web-components';

import {
  data,
  timezonesOptions,
} from 'web-utils';

import * as S from './styled';

const mask = (userInput = '') => {
  const resMask = [/[1-9]/, '\''];
  if (userInput[2] === '1') {
    resMask.push(/[1]/, /[0-1]/, '"');
  } else {
    resMask.push(/[0-9]/, '"');
  }
  return resMask;
};

const validationSchema = Yup.object().shape({
  country: Yup.string()
    .required('Please select your country.'),
  timezone: Yup.string()
    .required('Please select your timezone.'),
});

const mapState = state => ({
  user: coreSelectors.getProfile(state),
  fileUpload: getUiState('fileUpload')(state),
});

const ProfileSetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    user,
    fileUpload,
  } = useSelector(mapState, shallowEqual);
  const isClient = user.id && !user.is_pro;

  const onSubmit = (values: any, actions: any) => {
    const {
      height,
      units,
      ...rest
    } = values;
    dispatch(
      coreActions.profileSetupRequest({
        payload: {
          ...rest,
          height: (height && units === 'imperial')
            ? data.feetInchesToInches(height)
            : height || null,
        },
        settings: {
          is_completed_profile_customization: true,
          units_system: units,
        },
        callbacks: {
          ...actions,
          onSuccess: () => {
            const redirect = isClient ? '/welcome' : '/site_personalization';
            navigate(redirect);
          },
        },
      }),
    );
  };

  return (
    <S.MainContainer>
      <S.Title>
        Customize Your Profile
      </S.Title>
      <Formik
        initialValues={{
          timezone: '',
          birthday: '',
          gender: null,
          country: '',
          state: '',
          city: '',
          phone_number: '',
          height: '',
          weight: '',
          bodyfat: '',
          primary_goal: 'any',
          units: 'imperial',
        }}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(formikProps) => (
          <S.FormContainer
            as="form"
            onSubmit={formikProps.handleSubmit}
          >
            <Flex>
              <UploadPhoto
                width={150}
                height={150}
                src={user.user_profile?.avatar?.large?.url}
                placeholder={icons.clientAvatarPlaceHolder}
                uploadProgress={fileUpload[user.user_profile?.id]?.progress || 0}
                onChange={(file) => {
                  dispatch(
                    coreActions.requestUploadClientAvatar({
                      file,
                      profileId: user.user_profile?.id,
                    }),
                  );
                }}
                setPhotoPreview={() => {}}
              />
            </Flex>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                Country *
              </Text>
              <Field
                name="country"
                options={data.countriesOptions}
                component={SelectField}
                maxMenuHeight={500}
                placeholder="-- Select Country --"
                theme={{
                  borderRadius: 5,
                }}
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                State/Province
              </Text>
              <Field
                name="state"
                component={TextField}
                placeholder="Your state"
                color="#000"
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                City
              </Text>
              <Field
                name="city"
                component={TextField}
                placeholder="Your city"
                color="#000"
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                Time Zone *
              </Text>
              <Field
                name="timezone"
                options={timezonesOptions}
                component={SelectField}
                isSearchable={false}
                maxMenuHeight={500}
                placeholder="-- Select Time Zone --"
                theme={{
                  borderRadius: 5,
                }}
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                Birthday
              </Text>
              <Field
                name="birthday"
                placeholderText="Your Birthday"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                component={DatePickerField}
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                Gender
              </Text>
              <Field
                name="gender"
                options={data.genderOptions}
                component={SelectField}
                isSearchable={false}
                maxMenuHeight={500}
                placeholder="-- Select Your Gender --"
                theme={{
                  borderRadius: 5,
                }}
                styles={{
                  option: provided => ({
                    ...provided,
                    fontSize: '14px',
                    lineHeight: '12px',
                    fontWeight: 500,
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    fontSize: '15px',
                    lineHeight: '15px',
                    fontWeight: 500,
                  }),
                }}
              />
            </S.FieldContainer>
            <S.FieldContainer>
              <Text
                color="#000"
                weight={800}
                size="15px"
              >
                Units
              </Text>
              <Field
                name="units"
                options={data.unitsOptions}
                component={SelectField}
                isSearchable={false}
                maxMenuHeight={500}
                placeholder="-- Select Your Gender --"
                theme={{
                  borderRadius: 5,
                }}
                styles={{
                  option: provided => ({
                    ...provided,
                    fontSize: '14px',
                    lineHeight: '12px',
                    fontWeight: 500,
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    fontSize: '15px',
                    lineHeight: '15px',
                    fontWeight: 500,
                  }),
                }}
              />
            </S.FieldContainer>
            {isClient
              ? (
                <>
                  <S.FieldContainer>
                    <Text
                      color="#000"
                      weight={800}
                      size="15px"
                    >
                      {`Height (${formikProps.values.units === 'imperial' ? 'ft\' in' : 'cm'})`}
                    </Text>
                    <Field
                      mask={formikProps.values.units === 'imperial' ? mask : null}
                      name="height"
                      component={TextField}
                      autoComplete="off"
                    />
                  </S.FieldContainer>
                  <S.FieldContainer>
                    <Text
                      color="#000"
                      weight={800}
                      size="15px"
                    >
                      {`Weight (${formikProps.values.units === 'imperial' ? 'lbs' : 'kg'})`}
                    </Text>
                    <Field
                      name="weight"
                      component={TextField}
                      autoComplete="off"
                    />
                  </S.FieldContainer>
                  <S.FieldContainer>
                    <Text
                      color="#000"
                      weight={800}
                      size="15px"
                    >
                      Bodyfat
                    </Text>
                    <Field
                      name="bodyfat"
                      component={TextField}
                      autoComplete="off"
                    />
                  </S.FieldContainer>
                </>
              ) : (
                <S.FieldContainer>
                  <Text
                    color="#000"
                    weight={800}
                    size="15px"
                  >
                    Phone Number (optional)
                  </Text>
                  <Field
                    name="phone_number"
                    component={TextField}
                    placeholder="Your phone number"
                    color="#000"
                  />
                </S.FieldContainer>
              )}
            <Flex
              width="100%"
              justify="center"
              align="center"
            >
              <Button
                fit
                type="submit"
                spacing="big"
                isLoading={formikProps.isSubmitting}
                appearance="primaryBright"
                onClick={() => {
                  formikProps.validateForm().then(errors => {
                    Object.keys(errors)
                      .reverse()
                      .forEach(key => {
                        toast.error(errors[key]);
                      });
                  });
                }}
              >
                Next
              </Button>
            </Flex>
          </S.FormContainer>
        )}
      </Formik>
    </S.MainContainer>
  );
};

export default ProfileSetup;
